import { useEffect, useMemo, useState } from "react";
import { fmtEuro, jahrLabel, sortDe, uid } from "../kern/format.js";
import { FLASCHENGROESSEN } from "../kern/konstanten.js";
import { einstandsKarte } from "../kern/fachlogik.js";
import { eintragEntfernen, eintragHinzufuegen, eintragKonsumieren, eintragMenge, flaschenSumme, groessenMitBestand, karteEntfernen, weineMitBestand } from "../dienste/weinkartebuchung.js";
import { Auswahl, Btn, Eingabe, Feld, Leer, LoeschenKnopf, SuchAuswahl, TextKnopf } from "../ui/primitive.jsx";
import { FELDER_LABEL, felderVon } from "../kern/weinkarte.js";
import { weinkartePdfLaden } from "../dienste/weinkartepdf.js";
import { QrCode, qrPngLaden } from "../ui/qrcode.jsx";
import { SPRACHEN, t } from "../i18n/index.js";

/* ================================================================
   Teilen — geteilter Keller & Weinkarten.
   Freigaben liegen serverseitig (Token in der URL, jederzeit
   widerrufbar). Weinkarten sind Teil des Kellerbuchs und werden
   über die normale Synchronisation gespeichert.
   ================================================================ */

const teilLink = (token) => `${window.location.origin}/geteilt/${token}`;

async function ladeFreigaben() {
  const r = await fetch("/api/freigaben");
  if (!r.ok) throw new Error("Freigaben konnten nicht geladen werden.");
  return (await r.json()).freigaben || [];
}
async function erstelleFreigabe(koerper) {
  const r = await fetch("/api/freigaben", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(koerper),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.fehler || "Die Freigabe konnte nicht erstellt werden.");
  return json.token;
}
async function widerrufeFreigabe(token) {
  const r = await fetch("/api/freigaben/" + encodeURIComponent(token), { method: "DELETE" });
  if (!r.ok) throw new Error("Die Freigabe konnte nicht widerrufen werden.");
}

/* Kleiner Link-Baustein mit Kopieren und Widerrufen. */
function LinkZeile({ token, onWiderruf, mitQr = true, dateiname = null }) {
  const [kopiert, setKopiert] = useState(false);
  const link = teilLink(token);
  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1500);
    } catch (e) { /* Clipboard evtl. gesperrt — Nutzer kann markieren */ }
  };
  return (
    <div className="wl-teil-link">
      <input className="wl-eingabe" readOnly value={link} onFocus={(e) => e.target.select()} />
      <Btn variante="linie" onClick={kopieren}>{kopiert ? "Kopiert" : "Kopieren"}</Btn>
      <TextKnopf className="wl-teil-widerruf" onClick={onWiderruf}>{t("Widerrufen")}</TextKnopf>
      {mitQr && (
        <div className="wl-qr-block">
          <QrCode text={link} groesse={150} />
          <TextKnopf onClick={() => qrPngLaden(link, (dateiname || "winelake") + "-qr.png")}>
            QR-Code als PNG speichern
          </TextKnopf>
        </div>
      )}
    </div>
  );
}

/* — Keller teilen ————————————————————————————————————————————— */

function TeilenPanel({ onClose }) {
  const [freigaben, setFreigaben] = useState(null); /* null = lädt */
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = async () => {
    try { setFreigaben((await ladeFreigaben()).filter((f) => f.typ === "keller")); }
    catch (e) { setFehler(e.message); setFreigaben([]); }
  };
  useEffect(() => { laden(); }, []);

  const neu = async () => {
    setLaeuft(true); setFehler(null);
    try { await erstelleFreigabe({ typ: "keller" }); await laden(); }
    catch (e) { setFehler(e.message); }
    finally { setLaeuft(false); }
  };
  const widerruf = async (token) => {
    setFehler(null);
    try { await widerrufeFreigabe(token); await laden(); }
    catch (e) { setFehler(e.message); }
  };

  return (
    <div className="wl-teil">
      <p className="wl-teil-info">
        Über einen Link können andere deinen Keller — Weine, Winzer, Regionen und Bestände —
        ansehen. Einkaufspreise, Historie und Notizen bleiben verborgen, und verändern lässt
        sich nichts. Du kannst einen Link jederzeit widerrufen.
      </p>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      {freigaben === null ? (
        <p className="wl-teil-info">{t("Wird geladen …")}</p>
      ) : freigaben.length === 0 ? (
        <Leer text={t("Noch kein Freigabe-Link vorhanden.")} />
      ) : (
        freigaben.map((f) => <LinkZeile key={f.token} token={f.token} onWiderruf={() => widerruf(f.token)} />)
      )}
      <div className="wl-teil-fuss">
        <Btn variante="voll" onClick={neu} disabled={laeuft}>
          {laeuft ? "Einen Moment …" : freigaben && freigaben.length ? "Weiteren Link erstellen" : "Freigabe-Link erstellen"}
        </Btn>
        <Btn variante="linie" onClick={onClose}>{t("Schließen")}</Btn>
      </div>
    </div>
  );
}

/* — Weinkarten ———————————————————————————————————————————————— */

/* Wein, Jahrgang, Flaschengröße und Menge wählen. Angeboten wird nur,
   was wirklich im Keller liegt — eine Weinkarte führt Flaschen, keine
   Wunschliste. */
function EintragHinzufuegen({ db, winzerById, regionById, onAdd, fehler }) {
  const [weinId, setWeinId] = useState("");
  const [jgId, setJgId] = useState("");
  const [groesse, setGroesse] = useState("");
  const [menge, setMenge] = useState("1");
  const [preis, setPreis] = useState("");

  const weine = useMemo(() => weineMitBestand(db), [db]);
  const wein = weine.find((w) => w.id === weinId) || null;
  const jahrgaenge = useMemo(
    () => wein ? (wein.jahrgaenge || []).filter((j) => flaschenSumme(j) > 0) : [], [wein]);
  const jg = jahrgaenge.find((j) => j.id === jgId) || null;
  const groessen = useMemo(() => groessenMitBestand(jg), [jg]);
  const frei = jg && groesse ? (Number((jg.bestaende || {})[groesse]) || 0) : 0;

  /* Mittlerer Einstand dieses Jahrgangs — aus den Einlagerungen
     gemittelt. Hilft, den Kartenpreis einzuordnen. Fehlt er in den
     Buchungen, greift der am Jahrgang hinterlegte Preis. */
  const einstand = useMemo(() => {
    if (!jg) return null;
    const e = einstandsKarte(db).get(jg.id);
    if (e && e.menge > 0) return e.summe / e.menge;
    return jg.preis != null ? jg.preis : null;
  }, [db, jg]);

  /* Sobald die Auswahl eindeutig ist, gleich vorbelegen. */
  useEffect(() => {
    if (jahrgaenge.length === 1 && !jgId) setJgId(jahrgaenge[0].id);
  }, [jahrgaenge]);
  useEffect(() => {
    if (groessen.length >= 1 && !groessen.includes(groesse)) setGroesse(groessen[0] || "");
  }, [groessen]);

  const neben = (w) => {
    const wz = w.winzerId ? winzerById.get(w.winzerId) : null;
    const reg = (w.regionId && regionById.get(w.regionId)) || (wz && regionById.get(wz.regionId)) || null;
    return [wz && wz.name, reg && reg.name].filter(Boolean).join(" · ");
  };

  const hinzufuegen = () => {
    if (!wein || !jg || !groesse) return;
    const p = parseFloat(String(preis).replace(",", "."));
    const ok = onAdd({
      weinId, jgId, groesse,
      menge: parseInt(menge, 10) || 0,
      preis: Number.isFinite(p) ? p : null,
    });
    if (ok) {
      setWeinId(""); setJgId(""); setGroesse(""); setMenge("1"); setPreis("");
    }
  };

  if (weine.length === 0) {
    return <Leer text={t("Im Keller liegen keine Flaschen, die sich auf eine Weinkarte setzen ließen.")} />;
  }

  return (
    <div className="wl-karte-neu">
      <Feld label={t("Wein")} breit>
        <SuchAuswahl value={weinId} platzhalter={t("Wein aus dem Keller wählen …")}
          onChange={(e) => { setWeinId(e.target.value); setJgId(""); setGroesse(""); }}
          optionen={weine.slice().sort(sortDe).map((w) => ({ wert: w.id, label: w.name, neben: neben(w) }))} />
      </Feld>
      <div className="wl-karte-neu-zeile">
        <Feld label={t("Jahrgang")}>
          <Auswahl value={jgId} onChange={(e) => { setJgId(e.target.value); setGroesse(""); }} disabled={!wein}>
            <option value="">{t("Wählen …")}</option>
            {jahrgaenge.map((j) => (
              <option key={j.id} value={j.id}>{jahrLabel(j.jahr)} · {flaschenSumme(j)} Fl.</option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("Größe")}>
          <Auswahl value={groesse} onChange={(e) => setGroesse(e.target.value)} disabled={!jg}>
            {groessen.length === 0 && <option value="">—</option>}
            {groessen.map((g) => {
              const fg = FLASCHENGROESSEN.find((x) => String(x.key) === String(g));
              return <option key={g} value={g}>{fg ? fg.kurz : g} · {(jg.bestaende || {})[g]} Fl.</option>;
            })}
          </Auswahl>
        </Feld>
      </div>
      <div className="wl-karte-neu-zeile">
        <Feld label={frei ? `Menge (max. ${frei})` : "Menge"}>
          <Eingabe type="number" inputMode="numeric" min="1" max={frei || 1} value={menge}
            onChange={(e) => setMenge(e.target.value)} disabled={!frei} />
        </Feld>
        <Feld label={t("Preis (€)")}>
          <Eingabe inputMode="decimal" value={preis} placeholder="z. B. 39"
            onChange={(e) => setPreis(e.target.value)} />
        </Feld>
      </div>
      {einstand != null && (
        <p className="wl-karte-einstand">
          Mittlerer Einstand dieses Jahrgangs: <strong>{fmtEuro(einstand)}</strong> je Flasche
        </p>
      )}
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <Btn variante="voll" onClick={hinzufuegen} disabled={!wein || !jg || !groesse || !frei}>
        Auf die Weinkarte setzen
      </Btn>
    </div>
  );
}

function KarteEditor({ karte, db, winzerById, regionById, freigabe, aktionen, onShare, onWiderruf, onZurueck }) {
  const [fehler, setFehler] = useState(null);
  const [listenSuche, setListenSuche] = useState("");
  /* Die Liste als Ganzes lässt sich zuklappen — bei vielen Positionen
     ist der Weg zu den Einstellungen darunter sonst weit. Eine Suche
     klappt sie selbsttätig wieder auf. */
  const [listeZu, setListeZu] = useState(false);

  const felder = felderVon(karte);
  const setzeFeld = (schluessel, wert) => aktionen.setzeFelder(karte.id, { ...felder, [schluessel]: wert });

  const beschr = (e) => {
    const w = (db.weine || []).find((x) => x.id === e.weinId);
    if (!w) return { name: "(gelöschter Wein)", neben: "", jahr: null, groesse: "" };
    const wz = w.winzerId ? winzerById.get(w.winzerId) : null;
    const reg = (w.regionId && regionById.get(w.regionId)) || (wz && regionById.get(wz.regionId)) || null;
    const jg = e.jgId ? (w.jahrgaenge || []).find((j) => j.id === e.jgId) : null;
    const fg = e.groesse ? FLASCHENGROESSEN.find((x) => String(x.key) === String(e.groesse)) : null;
    return {
      name: w.name,
      neben: [wz && wz.name, reg && reg.name].filter(Boolean).join(" · "),
      jahr: jg ? jg.jahr : null,
      groesse: fg ? fg.kurz : e.groesse || "",
    };
  };

  /* Positionen nach Wein gruppiert — so lässt sich jeder Wein einzeln
     ein- und ausklappen und die Liste bleibt auch bei vielen Karten-
     positionen übersichtlich. */
  const gruppen = useMemo(() => {
    const nach = new Map();
    for (const e of karte.eintraege || []) {
      const b = beschr(e);
      const schl = e.weinId || b.name;
      if (!nach.has(schl)) nach.set(schl, { schl, name: b.name, neben: b.neben, posten: [] });
      nach.get(schl).posten.push({ e, b });
    }
    return [...nach.values()].sort((a, b) => String(a.name).localeCompare(String(b.name), "de"));
  }, [karte.eintraege, db]);

  const q = listenSuche.trim().toLowerCase();
  const sichtbar = q
    ? gruppen.filter((g) => `${g.name} ${g.neben} ${g.posten.map((p) => jahrLabel(p.b.jahr)).join(" ")}`
        .toLowerCase().includes(q))
    : gruppen;

  const offen = !listeZu || !!q;
  const flaschenIn = (g) => g.posten.reduce((s, p) => s + (p.e.menge || 0), 0);

  const position = (e, b) => (
    <li key={e.id || e.weinId} className="wl-karte-pos">
      <div className="wl-karte-pos-text">
        <span className="wl-karte-pos-name">
          {b.jahr != null ? jahrLabel(b.jahr) : "—"}
          {b.groesse ? ` · ${b.groesse}` : ""}
        </span>
      </div>
      {e.menge != null && (
        <div className="wl-karte-pos-menge">
          <Eingabe type="number" inputMode="numeric" min="1" value={String(e.menge)}
            onChange={(ev) => {
              const f = aktionen.setzeMenge(karte.id, e.id, ev.target.value);
              setFehler(f);
            }} />
          <span className="wl-karte-eur">{t("Fl.")}</span>
        </div>
      )}
      <div className="wl-karte-pos-preis">
        <Eingabe inputMode="decimal" value={e.preis != null ? String(e.preis) : ""}
          placeholder={t("Preis")} onChange={(ev) => aktionen.setzePreis(karte.id, e.id, ev.target.value)} />
        <span className="wl-karte-eur">€</span>
      </div>
      <div className="wl-karte-pos-aktionen">
        {e.menge != null && (
          <TextKnopf title={t("Eine Flasche als ausgeschenkt buchen")}
            onClick={() => {
              const frage = `Eine Flasche ${b.name}`
                + (b.jahr != null ? ` ${jahrLabel(b.jahr)}` : "")
                + " als ausgeschenkt buchen? Sie wird damit als getrunken gezählt "
                + "und kehrt nicht in den Keller zurück.";
              if (!window.confirm(frage)) return;
              setFehler(aktionen.konsumiere(karte.id, e.id));
            }}>
            Ausgeschenkt
          </TextKnopf>
        )}
        <TextKnopf className="wl-teil-widerruf" title={t("Zurück in den Keller")}
          onClick={() => { aktionen.entferne(karte.id, e.id); setFehler(null); }}
          aria-label={t("Zurück in den Keller")}>×</TextKnopf>
      </div>
    </li>
  );

  return (
    <div className="wl-karte-editor">
      <TextKnopf onClick={onZurueck}>‹ Übersicht</TextKnopf>
      <Feld label={t("Titel der Weinkarte")} breit>
        <Eingabe value={karte.titel} onChange={(e) => aktionen.umbenennen(karte.id, e.target.value)} />
      </Feld>

      {karte.eintraege.length === 0
        ? <Leer text={t("Noch keine Flaschen auf dieser Karte.")} />
        : (
          <>
            <div className="wl-karte-listenkopf">
              <button className="wl-karte-listenschalter" onClick={() => setListeZu((v) => !v)}
                aria-expanded={offen} disabled={!!q}>
                <span className="wl-karte-pfeil" aria-hidden="true">{offen ? "▾" : "▸"}</span>
                <span>{t.plural(gruppen.length, "{n} Wein", "{n} Weine")}</span>
              </button>
              <Eingabe className="wl-karte-listensuche" value={listenSuche}
                placeholder={t("Wein auf der Karte suchen …")}
                onChange={(e) => setListenSuche(e.target.value)} />
            </div>
            {!offen ? null : sichtbar.length === 0
              ? <Leer text={t("Kein Wein auf der Karte passt zur Suche.")} />
              : (
                <ul className="wl-karte-gruppen">
                  {sichtbar.map((g) => (
                    <li key={g.schl} className="wl-karte-gruppe">
                      <div className="wl-karte-gruppenkopf">
                        <span className="wl-karte-gruppenname">{g.name}</span>
                        {g.neben && <span className="wl-karte-gruppenneben">{g.neben}</span>}
                        <span className="wl-karte-gruppeninfo">
                          {t.plural(g.posten.length, "{n} Position", "{n} Positionen")}
                          {flaschenIn(g) ? ` · ${flaschenIn(g)} Fl.` : ""}
                        </span>
                      </div>
                      <ul className="wl-karte-liste">
                        {g.posten.map(({ e, b }) => position(e, b))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
          </>
        )}

      {fehler && <p className="wl-fehler">{fehler}</p>}
      <p className="wl-teil-info">
        Flaschen auf der Weinkarte sind dem Keller entnommen. „Ausgeschenkt“ bucht
        eine Flasche als konsumiert — sie erscheint dann in der Statistik unter
        „Aus Weinkarten“. Über × wandern die restlichen Flaschen zurück in den Keller.
      </p>

      <EintragHinzufuegen db={db} winzerById={winzerById} regionById={regionById}
        fehler={null}
        onAdd={(eintrag) => {
          const f = aktionen.hinzufuegen(karte.id, eintrag);
          setFehler(f);
          return !f;
        }} />

      <div className="wl-teil-trenner" />
      <span className="wl-konto-titel">{t("Sichtbare Angaben")}</span>
      <p className="wl-teil-info">
        Bestimmt, was auf der geteilten Karte und im PDF erscheint. Verborgene
        Angaben werden gar nicht erst übertragen.
      </p>
      <div className="wl-karte-felder">
        {Object.keys(FELDER_LABEL).map((k) => (
          <label key={k} className="wl-karte-feld">
            <input type="checkbox" checked={felder[k]} onChange={(e) => setzeFeld(k, e.target.checked)} />
            <span>{FELDER_LABEL[k]}</span>
          </label>
        ))}
      </div>

      <div className="wl-teil-trenner" />
      <span className="wl-konto-titel">{t("Sprache der Karte")}</span>
      <p className="wl-teil-info">
        Bestimmt, in welcher Sprache die geteilte Karte zuerst erscheint. Gäste
        können jederzeit umschalten; Weinnamen, Winzer und Regionen bleiben
        immer so, wie du sie erfasst hast.
      </p>
      <Auswahl value={karte.sprache || ""} aria-label={t("Sprache der Karte")}
        onChange={(e) => aktionen.setzeSprache(karte.id, e.target.value || null)}>
        <option value="">{t("Automatisch (Sprache des Gastes)")}</option>
        {SPRACHEN.map((sp) => <option key={sp.code} value={sp.code}>{sp.name}</option>)}
      </Auswahl>

      <div className="wl-teil-trenner" />
      <span className="wl-konto-titel">PDF</span>
      <p className="wl-teil-info">
        Erzeugt die Weinkarte als PDF-Datei. Es erscheinen die oben
        freigegebenen Angaben.
      </p>
      <Feld label={t("Gliederung")} breit>
        <Auswahl value={karte.pdfGliederung === "label" ? "label" : "herkunft"}
          onChange={(e) => aktionen.setzeOption(karte.id, "pdfGliederung", e.target.value)}>
          <option value="herkunft">{t("Land → Region → Winzer → Weine")}</option>
          <option value="label">{t("Label → Land → Region → Winzer → Weine")}</option>
        </Auswahl>
      </Feld>
      {karte.pdfGliederung === "label" && (
        <p className="wl-teil-info">
          Weine ohne Label stehen am Ende unter „Weitere Weine“. Die
          Label-Überschriften erscheinen im PDF unabhängig davon, ob „Label“
          oben freigegeben ist.
        </p>
      )}
      <div className="wl-karte-felder">
        <label className="wl-karte-feld">
          <input type="checkbox" checked={!!karte.pdfInhalt}
            onChange={(e) => aktionen.setzeOption(karte.id, "pdfInhalt", e.target.checked)} />
          <span>{t("Inhaltsverzeichnis (Länder & Regionen)")}</span>
        </label>
        <label className="wl-karte-feld">
          <input type="checkbox" checked={!!karte.pdfSeiten}
            onChange={(e) => aktionen.setzeOption(karte.id, "pdfSeiten", e.target.checked)} />
          <span>{t("Seitenzahlen")}</span>
        </label>
      </div>
      {karte.pdfInhalt && !felder.land && !felder.region && karte.pdfGliederung !== "label" && (
        <p className="wl-teil-info">
          Für das Inhaltsverzeichnis müssen Land oder Region unter „Sichtbare
          Angaben“ freigegeben sein.
        </p>
      )}
      <Btn variante="linie" onClick={() => weinkartePdfLaden(db, karte)}>
        {t("PDF herunterladen")}
      </Btn>

      <div className="wl-teil-trenner" />
      <p className="wl-teil-info">
        Teile die Weinkarte über einen Link. Änderungen an Preisen oder Mengen
        erscheinen automatisch, sobald sie synchronisiert sind.
      </p>
      {freigabe
        ? <LinkZeile token={freigabe.token} onWiderruf={onWiderruf}
            dateiname={(karte.titel || "weinkarte").toLowerCase().replace(/[^a-z0-9]+/g, "-")} />
        : <Btn variante="voll" onClick={onShare}>{t("Diese Weinkarte teilen")}</Btn>}
    </div>
  );
}

function WeinkartenPanel({ db, setDb, onClose }) {
  const [auswahlId, setAuswahlId] = useState(null);
  const [titel, setTitel] = useState("");
  const [freigaben, setFreigaben] = useState([]);
  const [fehler, setFehler] = useState(null);

  const weinkarten = db.weinkarten || [];
  const winzerById = new Map((db.winzer || []).map((w) => [w.id, w]));
  const regionById = new Map((db.regions || []).map((r) => [r.id, r]));

  const ladenFreigaben = async () => {
    try { setFreigaben((await ladeFreigaben()).filter((f) => f.typ === "weinkarte")); }
    catch (e) { /* Freigabe-Status ist Beiwerk */ }
  };
  useEffect(() => { ladenFreigaben(); }, []);

  const freigabeVon = (karteId) => freigaben.find((f) => f.weinkarteId === karteId) || null;

  /* Alle Eingriffe laufen über eine Stelle — Bestand, Karte und
     Bewegungsprotokoll müssen zusammen geändert werden. */
  const aktionen = {
    hinzufuegen: (karteId, eintrag) => {
      let meldung = null;
      setDb((prev) => {
        const { db: neu, fehler: f } = eintragHinzufuegen(prev, karteId, eintrag);
        meldung = f;
        return f ? prev : neu;
      });
      return meldung;
    },
    setzeMenge: (karteId, eintragId, wert) => {
      let meldung = null;
      setDb((prev) => {
        const { db: neu, fehler: f } = eintragMenge(prev, karteId, eintragId, wert);
        meldung = f;
        return f ? prev : neu;
      });
      return meldung;
    },
    entferne: (karteId, eintragId) =>
      setDb((prev) => eintragEntfernen(prev, karteId, eintragId).db),
    konsumiere: (karteId, eintragId) => {
      let meldung = null;
      setDb((prev) => {
        const { db: neu, fehler: f } = eintragKonsumieren(prev, karteId, eintragId, 1);
        meldung = f;
        return f ? prev : neu;
      });
      return meldung;
    },
    setzePreis: (karteId, eintragId, wert) => {
      const p = parseFloat(String(wert).replace(",", "."));
      setDb((prev) => ({
        ...prev,
        weinkarten: (prev.weinkarten || []).map((k) => k.id !== karteId ? k : {
          ...k,
          eintraege: k.eintraege.map((e) => e.id === eintragId
            ? { ...e, preis: Number.isFinite(p) ? p : null } : e),
        }),
      }));
    },
    umbenennen: (karteId, neuerTitel) => setDb((prev) => ({
      ...prev,
      weinkarten: (prev.weinkarten || []).map((k) => k.id === karteId ? { ...k, titel: neuerTitel } : k),
    })),
    setzeSprache: (karteId, sprache) => setDb((prev) => ({
      ...prev,
      weinkarten: (prev.weinkarten || []).map((k) => k.id === karteId ? { ...k, sprache } : k),
    })),
    /* Einzelne Karteneigenschaften (z. B. PDF-Optionen). */
    setzeOption: (karteId, schluessel, wert) => setDb((prev) => ({
      ...prev,
      weinkarten: (prev.weinkarten || []).map((k) => k.id === karteId ? { ...k, [schluessel]: wert } : k),
    })),
    setzeFelder: (karteId, felder) => setDb((prev) => ({
      ...prev,
      weinkarten: (prev.weinkarten || []).map((k) => k.id === karteId ? { ...k, felder } : k),
    })),
  };

  const anlegen = () => {
    const t = titel.trim();
    if (!t) return;
    const id = uid();
    setDb((prev) => ({
      ...prev,
      weinkarten: [...(prev.weinkarten || []),
        { id, titel: t, erstellt: new Date().toISOString(), eintraege: [] }],
    }));
    setTitel("");
    setAuswahlId(id);
  };

  const loeschen = async (karte) => {
    const f = freigabeVon(karte.id);
    if (f) { try { await widerrufeFreigabe(f.token); } catch (e) { /* egal */ } await ladenFreigaben(); }
    setDb((prev) => karteEntfernen(prev, karte.id));
    if (auswahlId === karte.id) setAuswahlId(null);
  };

  const teile = async (karte) => {
    setFehler(null);
    try { await erstelleFreigabe({ typ: "weinkarte", weinkarteId: karte.id, titel: karte.titel }); await ladenFreigaben(); }
    catch (e) { setFehler(e.message); }
  };
  const widerruf = async (karte) => {
    const f = freigabeVon(karte.id);
    if (!f) return;
    try { await widerrufeFreigabe(f.token); await ladenFreigaben(); }
    catch (e) { setFehler(e.message); }
  };

  const aktuelle = auswahlId ? weinkarten.find((k) => k.id === auswahlId) : null;

  if (aktuelle) {
    return (
      <div className="wl-teil">
        {fehler && <p className="wl-fehler">{fehler}</p>}
        <KarteEditor karte={aktuelle} db={db} winzerById={winzerById} regionById={regionById}
          freigabe={freigabeVon(aktuelle.id)} aktionen={aktionen}
          onShare={() => teile(aktuelle)} onWiderruf={() => widerruf(aktuelle)}
          onZurueck={() => setAuswahlId(null)} />
        <div className="wl-teil-fuss">
          <Btn variante="linie" onClick={onClose}>{t("Schließen")}</Btn>
        </div>
      </div>
    );
  }

  const flaschenAufKarte = (k) => (k.eintraege || []).reduce((s, e) => s + (e.menge || 0), 0);

  return (
    <div className="wl-teil">
      <p className="wl-teil-info">
        Stelle Weinkarten aus deinem Keller zusammen: Flaschen wandern aus dem
        Bestand auf die Karte und bei Bedarf zurück. Jede Karte lässt sich über
        einen Link schreibgeschützt teilen.
      </p>
      {fehler && <p className="wl-fehler">{fehler}</p>}

      <div className="wl-karte-anlegen">
        <Eingabe value={titel} placeholder={t("Titel der neuen Weinkarte")} onChange={(e) => setTitel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") anlegen(); }} />
        <Btn variante="voll" onClick={anlegen} disabled={!titel.trim()}>{t("Anlegen")}</Btn>
      </div>

      {weinkarten.length === 0
        ? <Leer text={t("Noch keine Weinkarten.")} />
        : (
          <ul className="wl-karte-uebersicht">
            {weinkarten.slice().sort((a, b) => String(a.titel || "").localeCompare(String(b.titel || ""), "de")).map((k) => (
              <li key={k.id} className="wl-karte-zeile">
                <button className="wl-karte-zeile-text" onClick={() => setAuswahlId(k.id)}>
                  <span className="wl-karte-zeile-name">{k.titel}</span>
                  <span className="wl-karte-zeile-info">
                    {k.eintraege.length} {k.eintraege.length === 1 ? "Position" : "Positionen"}
                    {flaschenAufKarte(k) ? ` · ${flaschenAufKarte(k)} Fl.` : ""}
                    {freigabeVon(k.id) ? " · geteilt" : ""}
                  </span>
                </button>
                <div className="wl-karte-zeile-aktion">
                  <Btn variante="linie" onClick={() => setAuswahlId(k.id)}>{t("Bearbeiten")}</Btn>
                  <LoeschenKnopf onDelete={() => loeschen(k)} label={t("Löschen")} />
                </div>
              </li>
            ))}
          </ul>
        )}

      <div className="wl-teil-fuss">
        <Btn variante="linie" onClick={onClose}>{t("Schließen")}</Btn>
      </div>
    </div>
  );
}

export { TeilenPanel, WeinkartenPanel };
